import { createAction } from 'redux-starter-kit';
import uuid from 'uuid/v4';
import { message } from 'antd';
import ViaNetworkError from '../../../utility/error/ViaNetworkError';
import { addNotification } from '../../ui/application/messageUiActions';
import { resetViaState } from '../../viaActions';
import { selectUser } from '../users/userReducer';
import viaIntl from '../../../contexts/via-intl';


export const selectRequestUser = (state) => {
  const { userId } = state.via.ui.application.session;
  if (!userId) return null;

  return selectUser(state, userId);
};


/**
 * An action that signifies a request has been made.
 * Takes a request id. This should be a unique id.
 *
 * Note: The action requestStart() is a convenience function that generated the id automatically.
 *
 * @example dispatch(request(requestId))
 */
export const request = createAction('REQUEST');

/**
 * An action that signifies a request has succeded.
 * Takes a request id. This should match an existing request.
 * @example dispatch(requestSuccess(requestId))
 */
export const requestSuccess = createAction('REQUEST_SUCCESS');

/**
 * An action that signifies a request has failed.
 * Takes a request id. This should match an existing request.
 * @example dispatch(requestFailure(requestId))
 */
export const requestFailure = createAction('REQUEST_FAILURE');

/**
 * An action that signifies a request has been cancelled.
 * Takes a request id. This should match an existing request.
 * @example dispatch(requestCancel(requestId))
 */
export const requestCancel = createAction('REQUEST_CANCEL');

/**
 * A more convenient version of the 'REQUEST' action.
 * This generates an returns a uuid of the request.
 *
 * @param {string} [id] The id of the request. This is generated if not defined.
 * @returns {string} The id of the request.
 * @example const requestId = dispatch(requestStart())
 * @example const requestId = dispatch(requestStart('a-unique-id'));
 */
export const requestStart = (id = uuid()) => (dispatch) => {
  dispatch(request(id));
  return id;
};

/**
 * A more convenient action for 'REQUEST_FAILURE' action.
 * @param {string} requestId The id of the request that failed.
 * @param {string} error The error message of the failed request.
 */
export const requestFailed = (requestId, error = null) => (dispatch, getState) => {
  dispatch(requestFailure({
    id: requestId,
    payload: error && error.body ? {
      errorMessage: error.body.errorMessage || viaIntl.intl.formatMessage({ id: 'failureTryAgain' }),
      errors: error.body.errors || [viaIntl.intl.formatMessage({ id: 'failureDescription' })],
      code: error.code,
    } : null,
  }));

  if (error) {
    if (error instanceof ViaNetworkError) {
      switch (error.code) {
        case 400: // Validation Error
          if (error.body) message.warning(error.body.errorMessage || viaIntl.intl.formatMessage({ id: 'failureTryAgain' }));
          break;
        case 401: // Unauthorised
          // Multiple requests may fail at the same time, so we need to check if the other requests have already reset the via state.
          // Otherwise we end up reseting state many times and raising many signout notifications.
          if (selectRequestUser(getState())) {
            dispatch(resetViaState());
            dispatch(addNotification('warning', 0, 'sessionEnded', undefined, 'sessionRe-enter', undefined, 'TIMED_OUT_NOTIFICATION'));
          }
          break;
        case 403: // Forbidden
          // TODO: (CM)
          // The user doesn't have access to the data on this page. Either their permissions have changed and we should
          // reload their data, or we have made a coding error and the request should not have been made in the first place.
          dispatch(addNotification('error', 0, 'forbidden', undefined, 'forbiddenDescription'));
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('VIA: You have made a request without having permission on your user account. The user should not be able to interact with UI elements that make forbidden requests.');
          }
          break;
        case 404:
          // Nothing needs to happen in this scenario. Not a real error.
          break;
        case 504: // Gateway Timeout (API server unreachable when web server is reachable)
          dispatch(addNotification('warning', 0, 'unreachable', undefined, 'unreachableDescription'));
          break;
        default:
          dispatch(addNotification('error', 0, 'failureTryAgain', undefined, 'failureDescription'));
      }
    } else {
      // Assume only reason for this error is when the web server is unreachable.
      // eslint-disable-next-line no-console
      console.error(error);
      dispatch(addNotification('warning', 0, 'unreachable', undefined, 'unreachableDescription'));
    }
  }
};


/**
 * A more convenient action for 'REQUEST_SUCCESS'.
 * @param {string} requestId The id of the request that succeded.
 * @param {any} [payload] The result of the request. [IMPORTANT: This should be normalised!]
 */
export const requestSucceded = (requestId, payload = null) => (dispatch) => {
  dispatch(requestSuccess({ id: requestId, payload }));
};


/**
 * A more convenient action for 'REQUEST_CANCEL'.
 * @param {string} requestId The id of the request that has been cancelled.
 * @param {any} [payload] The result of the request. [IMPORTANT: This should be normalised!]
 */
export const requestCancelled = (requestId, payload = null) => (dispatch) => {
  dispatch(requestCancel({ id: requestId, payload }));
};
